import db from '@services/firebase-service';
import {
  doc, onSnapshot, updateDoc, arrayRemove
} from 'firebase/firestore';
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { getAnimationSpeed } from '@services/transient-thought.service';
import { useStreamData } from '@lib/hooks/useStreamData';
import { logToCloudWatch } from '@services/logger.service';

import TransientThoughtResult from './TransientThoughtResult';
import TransientThoughtLiveBubble from './TransientThoughtLiveBubble';
import TransientThoughtHistoryFeed from './TransientThoughtHistoryFeed';
import {
  useTransientThoughtChain, transientThoughtVariants
} from './TransientThoughts/useTransientThoughtChain';
import { useEngagementFeatureComments } from './useEngagementFeatureComments';

import { handleEngagement } from '@services/interactions-service';
import useFonts from '@lib/hooks/useFonts';
import useFontSizeCalculator from '@components/Presentation/helpers/fontSizeCalculator';
import useTransientThoughtHistory from '@lib/hooks/useTransientThoughtHistory';

export { transientThoughtVariants };

// Build per-slot Framer Motion variants using the comment's own reading time.
// Passed straight to TransientThoughtCloud / TransientThoughtSpeechBubble so
// the SVG path animation runs for exactly as long as the bubble is on screen.
const makeVariants = (rTime, speed) => ({
  visible: (delay) => ({
    pathLength: 1,
    transition: {
      duration: rTime,
      delay: delay + speed.start,
      ease: 'easeOut'
    }
  })
});

const noop = () => { };

const TransientThought = forwardRef(({
  streamId, currentRosResult, currentRosFeature, colorData, streamSettings, platformType,
  startInteraction, setCurrentFeatureId, streamSetting, interactionType,
  setShowRosModel, moderationModeRef, activeInteractionRef, updateFSMeta,
  setShowNotification, setStartTime, layoutMode, showInstantCustomisationPopup,
  setTotalResponders
}, ref) => {
  const {
    firestoreUnsub,
    setFirestoreUnsub,
    setFeatureId
  } = useStreamData(s => ({
    firestoreUnsub: s.firestoreUnsub,
    setFirestoreUnsub: s.setFirestoreUnsub,
    setFeatureId: s.setFeatureId
  }));

  const { isRos } = interactionType;
  const ttPlaybackSpeed = streamSetting?.ttPlaybackSpeed ?? 'default';

  const [ttId, setttId] = useState('');
  const [colors, setColors] = useState(currentRosFeature.color);
  const [settings, setSettings] = useState(currentRosFeature.setting);
  const [containerSize, setContainerSize] = useState(null);

  // Bodies of comments still in Firestore's commentList (pending + displayed) —
  // used to hide not-yet-animated comments from the history feed.
  const pendingBodiesRef = useRef(new Set());

  // ttId mirror so the (decoupled) chain's onConsume can arrayRemove against the
  // current doc without closing over a stale ttId.
  const ttIdRef = useRef('');
  useEffect(() => { ttIdRef.current = ttId; }, [ttId]);

  const liveAreaRef = useRef(null);
  const [isLiveAreaVisible, setIsLiveAreaVisible] = useState(true);
  const containerRef = useRef(null);

  // Drop a comment from the accumulated Firestore array once it has been
  // displayed, keeping the doc bounded.
  const removeDisplayedComment = (raw) => {
    if (!raw || !ttIdRef.current) return;
    const docRef = doc(db, 'streams', streamId, 'transientThought', ttIdRef.current);
    updateDoc(docRef, { commentList: arrayRemove(raw) }).catch((err) => {
      logToCloudWatch('Failed to remove displayed transient thought comment', { streamId, ttId: ttIdRef.current, err }, 'ERROR');
    });
  };

  // ─── animation engine ────────────────────────────────────────────────────────
  const {
    slotA, slotB, enqueue, reset, setPaused, disableSlotA, disableSlotB
  } = useTransientThoughtChain({
    playbackSpeed: ttPlaybackSpeed,
    feature: currentRosFeature,
    colors,
    onConsume: removeDisplayedComment
  });

  // ─── history / scroll ───────────────────────────────────────────────────────
  const {
    prevComments, showBackToTop, scrollRef, handleScroll, scrollToTop
  } = useTransientThoughtHistory({
    featureId: currentRosFeature.id, streamId, ttId, enabled: isRos
  });

  const { getFontStyle } = useFonts();
  const { fontFamily } = getFontStyle(settings?.FontStyle || 'Lobster');
  const { ttFontSize } = settings || {};
  const computedFontSize = useFontSizeCalculator(ttFontSize ? Number(ttFontSize) : 1, 3, 'rem');

  const animationSpeed = getAnimationSpeed(ttPlaybackSpeed);

  // ─── paused-state comment pagination ─────────────────────────────────────────
  const {
    comments: previousData,
    sentinelRef: transientThoughtSentinelRef,
    hasMore: hasMoreTransientThoughtComments,
    isLoading: isLoadingTransientThoughtComments,
    isLoadingMore: isLoadingMoreComments,
  } = useEngagementFeatureComments({
    engageType: 'transientThought',
    featureId: ttId,
    enabled: !startInteraction,
  });

  // ─── lifecycle helpers ───────────────────────────────────────────────────────
  const unsubscribeFromFirestore = () => {
    if (firestoreUnsub.unsub) firestoreUnsub.unsub();
  };

  const setupTransientThought = ({
    id, settings: newSettings, colors: newColors, noOfResponders
  }) => {
    reset();
    setCurrentFeatureId(id);
    setttId(id);
    setFeatureId(id);
    setColors(newColors);
    setSettings(newSettings);
    setTotalResponders(noOfResponders);
  };

  const handleTransientThought = async ({ interactionId = null, interactionResult = [] }) => {
    const response = await handleEngagement({ streamId, platformType, currentRosFeature, interactionId });
    if (response.status && response.entity) {
      setupTransientThought({
        id: response.entity.engagementId,
        settings: currentRosFeature.setting,
        colors: currentRosFeature.color,
        noOfResponders: response.entity.noOfResponders || 0,
        interactionResult
      });
      updateFSMeta({ activeInteraction: { id: response.entity.engagementId, type: 'transientThought' } });
    }
  };

  useEffect(() => {
    if (activeInteractionRef.current && activeInteractionRef.current?.type === 'transientThought') {
      setupTransientThought({
        id: activeInteractionRef.current.id,
        settings: activeInteractionRef.current.settings,
        colors: activeInteractionRef.current.colors,
        noOfResponders: activeInteractionRef.current.noOfResponders || 0,
        interactionResult: activeInteractionRef.current.interactionResult || []
      });
    }
  }, [activeInteractionRef.current]);

  const stopTransientThought = () => {
    unsubscribeFromFirestore();
  };

  const handleStartInteraction = () => {
    if (currentRosResult && currentRosResult.length > 0) {
      handleTransientThought({ interactionId: currentRosResult[0].id, interactionResult: currentRosResult });
    } else {
      handleTransientThought({ interactionId: null, interactionResult: [] });
    }
    setStartTime(Date.now());
  };

  const manageTransientThoughtInteraction = () => {
    if (startInteraction && !moderationModeRef.current) {
      handleStartInteraction();
    } else if (ttId) {
      stopTransientThought();
    } else if (currentRosResult && currentRosResult.length > 0) {
      // Remounted (e.g. navigated to another slide and back) into an
      // already-paused interaction — seed ttId from the server-fetched
      // result locally instead of re-hitting the start-engagement endpoint.
      setupTransientThought({
        id: currentRosResult[0].id,
        settings: currentRosFeature.setting,
        colors: currentRosFeature.color,
        noOfResponders: currentRosResult[0].noOfResponders || 0,
        interactionResult: currentRosResult,
      });
    }
  };

  useEffect(() => {
    if (!currentRosFeature.id) return;
    setShowRosModel(false);
    manageTransientThoughtInteraction();
  }, [currentRosFeature.id, startInteraction]);

  useEffect(() => {
    if (currentRosFeature.id) {
      setColors(currentRosFeature.color);
      setSettings(currentRosFeature.setting);
    }
  }, [currentRosFeature, currentRosResult]);

  // ─── Firestore subscription ──────────────────────────────────────────────────
  useEffect(() => {
    reset();

    if (!ttId || !isRos) {
      return () => unsubscribeFromFirestore();
    }

    const logData = { streamId, interactionType: 'transientThought', interactionId: ttId };

    setFirestoreUnsub({
      unsub: onSnapshot(
        doc(db, 'streams', streamId, 'transientThought', ttId),
        (document) => {
          if (!document.exists()) return;

          const { commentList = [] } = document.data();

          if (commentList.length > 0) setShowNotification(false);

          // Keep history feed up to date with what's still in Firestore.
          pendingBodiesRef.current = new Set(
            commentList.map(c => c?.words).filter(Boolean)
          );

          setTotalResponders(document.data().noOfResponders || 0);

          // Append-only: the chain dedups and never wipes its queue, so
          // arrayRemove-triggered snapshots can't drop pending comments.
          enqueue(commentList);
        },
        (error) => logToCloudWatch('Failed to subscribe to firestore', { ...logData, error }, 'ERROR')
      )
    });

    return () => unsubscribeFromFirestore();
  }, [ttId]);

  // ─── scroll / pause / resume ─────────────────────────────────────────────────
  useEffect(() => {
    const liveEl = liveAreaRef.current;
    const rootEl = scrollRef.current;
    if (!liveEl || !rootEl) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.9;
        setIsLiveAreaVisible(visible);
      },
      { root: rootEl, threshold: Array.from({ length: 21 }, (_, i) => i / 20) }
    );
    observer.observe(liveEl);
    return () => observer.disconnect();
  }, [startInteraction]);

  useEffect(() => {
    setPaused(!isLiveAreaVisible);
  }, [isLiveAreaVisible]);

  // ─── container size ──────────────────────────────────────────────────────────
  const getWindowSize = ({ width, height }) => {
    if (width >= 1440) return height >= 600 ? 'large' : 'medium';
    return 'small';
  };

  useEffect(() => {
    if (!containerRef.current) return;
    setContainerSize(getWindowSize(containerRef.current.getBoundingClientRect()));
  }, [showInstantCustomisationPopup]);

  useImperativeHandle(ref, () => ({
    async triggerEngagement({ interactionResult }) {
      if (interactionResult && interactionResult.length > 0) {
        await handleTransientThought({ interactionId: interactionResult[0].id, interactionResult });
      }
    }
  }));

  const isBubbleStyle = currentRosFeature.setting.transientThoughtDisplayStyle
    === transientThoughtVariants.bubbles;

  return (
    <div ref={containerRef} className={`w-full h-full relative ${layoutMode === 'square' ? 'scale-75' : ''}`}>
      {startInteraction ? (
        <>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="w-full h-full overflow-y-auto overflow-x-hidden scrollbar-on-hover"
          >
            {/* Live area — full viewport; ephemeral bubbles animate here. */}
            <div ref={liveAreaRef} className="relative w-full h-full">
              <TransientThoughtLiveBubble
                isBubbleStyle={slotA?.isBubble ?? isBubbleStyle}
                config={slotA}
                disableBubble={disableSlotA}
                initiateAnimation={noop}
                variants={slotA ? makeVariants(slotA.readingTime, animationSpeed) : {}}
                colors={colors}
                baseFillColor={colors?.chatBubble1}
                fontFamily={fontFamily}
                fontSize={computedFontSize}
                containerSize={containerSize}
                showUsername={currentRosFeature.setting.transientThoughtShowUsername}
              />
              <TransientThoughtLiveBubble
                isBubbleStyle={slotB?.isBubble ?? isBubbleStyle}
                config={slotB}
                disableBubble={disableSlotB}
                initiateAnimation={noop}
                variants={slotB ? makeVariants(slotB.readingTime, animationSpeed) : {}}
                colors={colors}
                baseFillColor={colors?.chatBubble2}
                fontFamily={fontFamily}
                fontSize={computedFontSize}
                containerSize={containerSize}
                showUsername={currentRosFeature.setting.transientThoughtShowUsername}
              />
            </div>

            {/* Previous comments — only shown after they've been animated through. */}
            <TransientThoughtHistoryFeed
              prevComments={prevComments.filter((c) => {
                const text = c?.body || c?.words;
                if (text === slotA?.words || text === slotB?.words) return false;
                if (pendingBodiesRef.current.has(text)) return false;
                return true;
              })}
              ttId={ttId}
              isBubbleStyle={isBubbleStyle}
              colors={colors}
              fontFamily={fontFamily}
              fontSize={computedFontSize}
              showUsername={currentRosFeature.setting.transientThoughtShowUsername}
            />
          </div>

          {showBackToTop && (
            <button
              type="button"
              onClick={() => {
                scrollToTop();
                // Programmatic scroll may settle without crossing the observer's
                // threshold, so force-resume the chain directly.
                setPaused(false);
              }}
              className="absolute bottom-5 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-4 py-2 rounded-full bg-black/75 hover:bg-black/90 text-white text-sm shadow-lg backdrop-blur-sm transition"
              aria-label="Back to top"
            >
              ↑ Back to top
            </button>
          )}
        </>
      ) : (
        <TransientThoughtResult
          data={previousData}
          settings={settings}
          colors={colors}
          fontFamily={fontFamily}
          fontSize={computedFontSize}
          isBubbleStyle={isBubbleStyle}
          ttId={ttId}
          isLoading={isLoadingTransientThoughtComments}
          hasMore={hasMoreTransientThoughtComments}
          isLoadingMore={isLoadingMoreComments}
          sentinelRef={transientThoughtSentinelRef}
        />
      )}
    </div>
  );
});

export default TransientThought;
